import { take } from 'rxjs/operators';
import { CompanyService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { withPagination, selectCurrentPageEntities, selectPaginationData, selectCurrentPage, updatePaginationData, setPage, skipWhilePageExists, setCurrentPage, getPaginationData } from '@ngneat/elf-pagination';
import { Company } from '@tcc-mono/vandijk/shared/data-access/models';
import { withEntities, selectActiveEntity, withActiveId, upsertEntities, setActiveId, hasEntity, getActiveId, deleteEntities } from '@ngneat/elf-entities';
import { createStore } from '@ngneat/elf';
import { inject, Injectable } from '@angular/core';
import { createRequestDataSource, withRequestsStatus } from '@ngneat/elf-requests';
import { EMPTY, map, tap, identity, Observable } from 'rxjs';
import { ElfUtil } from '@tcc-mono/shared/utils';
import { PaginatedResponse, Response } from '@tcc-mono/shared/data-access';


const store = createStore(
  { name: 'companies' },
  withEntities<Company>(),
  withPagination(),
  withActiveId(),
  withRequestsStatus()
);

const companiesDataSource = createRequestDataSource({
  data$: () => store.pipe(selectCurrentPageEntities()),
  requestKey: 'companies',
  dataKey: 'companies',
  store
});

@Injectable({
  providedIn: 'root'
})
export class CompaniesRepository {
  private readonly _companyService: CompanyService = inject(CompanyService);

  public companies$ = store.pipe(
    selectCurrentPageEntities()
  );

  public paginationData$ = store.pipe(
    selectPaginationData()
  );

  public currentPage$ = store.pipe(
    selectCurrentPage()
  );

  public activeCompany$ = store.pipe(
    selectActiveEntity()
  );

  public loading$ = companiesDataSource.data$().pipe(map(({ loading }) => loading));

  public setActiveCompany = (id: number) => {
    if (store.query(getActiveId) !== id) {
      store.update(
        setActiveId(id)
      );

      if (!store.query(hasEntity(id))) {
        this.getCompany(id)
          .pipe(take(1))
          .subscribe();
      }

    }
  }

  public setPage = (page: number, refreshPage = false) => {
    store.update(
      setCurrentPage(page)
    );

    this._getCompaniesByPage(page, refreshPage)
      .pipe(take(1))
      .subscribe();
  }

  private _getCompaniesByPage = (page: number | string, refreshPage = false) => {
    return this._companyService.getAll({ page })
      .pipe(
        tap(this._upsertCompanies),
        companiesDataSource.trackRequestStatus(),
        refreshPage ? identity : skipWhilePageExists(store, page)
      );
  }

  public getCompany = (id: number) => {
    const call = this._companyService.get(id)
      .pipe(
        map(({ data }) => data),
        tap((company: Company) => store.update(upsertEntities(company)))
      );

    return store.query(hasEntity(id)) ? EMPTY : call;
  }

  private _upsertCompanies = ({ data, meta }: PaginatedResponse<Company>) => {
    const pagination = ElfUtil.convertMetaToElfPagination(meta);

    store.update(
      upsertEntities(data),
      updatePaginationData(pagination),
      setPage(
        pagination.currentPage,
        data.map(c => c.id)
      ),
      companiesDataSource.setSuccess()
    );
  }

  public addCompany = (company: Company): Observable<Company> => {
    return this._companyService.post(company)
      .pipe(
        map(({ data }) => data as Company),
        tap((newCompany) => {
          store.update(
            upsertEntities(newCompany)
          );

          const { currentPage } = store.query(getPaginationData());

          this.setPage(
            currentPage,
            true
          );
        })
      );
  }

  public updateCompany = (company: Company): Observable<Company> => {
    return this._companyService.put(company.id, company)
      .pipe(
        map(({ data }) => data as Company),
        tap((updatedCompany) => {
          store.update(
            upsertEntities(updatedCompany)
          );
        })
      );
  }

  public removeCompany = (companyId: string | number) => {
    return this._companyService.delete(companyId)
      .pipe(
        tap(() => {
          store.update(
            deleteEntities(+companyId)
          );

          const { currentPage } = store.query(getPaginationData());

          this.setPage(
            currentPage,
            true
          );
        })
      )
  }

}
